import { provenanceDeclaration, provenanceTransformation } from "./provenance";
export * from "./provenance";
import { z } from "zod";

export {
  graphIsTimelineReady,
  InvalidBuildGraphError,
  normalizeBuildGraph,
  type NormalizedGraphProject,
} from "./build-graph";

export const objectId = z.string().regex(/^[0-9a-f]{24}$/);
export type ObjectId = z.infer<typeof objectId>;
export const workspaceId = z.string().min(1).max(100);
export const role = z.enum(["owner", "admin", "contributor", "viewer"]);
export type WorkspaceRole = z.infer<typeof role>;

export const releaseProfile = z.object({
  repository: z.string().trim().min(1).max(255).optional(),
  name: z.string().trim().min(1).max(100),
  environment: z.string().trim().min(1).max(100),
  branchPattern: z.string().trim().min(1).max(255),
});
export const releaseNotesCategory = z.object({
  name: z.string().trim().min(1).max(50),
  emoji: z.string().trim().min(1).max(20),
  color: z.string().trim().min(1).max(30),
  aliases: z.array(z.string().trim().min(1).max(50)),
});
export const releaseNotesFallbackCategory = z.object({
  name: z.string().trim().min(1).max(50),
  emoji: z.string().trim().min(1).max(20),
  color: z.string().trim().min(1).max(30),
});
export const releaseNotesPersonality = z.enum(["factual", "pragmatic", "playful"]);
export type ReleaseNotesPersonality = z.infer<typeof releaseNotesPersonality>;

export const releaseConfiguration = z.object({
  deploymentTargets: z.array(z.string().trim().min(1).max(100)).min(1),
  favoriteEnvironments: z.array(z.string().trim().min(1).max(100)),
  profiles: z.array(releaseProfile),
  releaseNotesPersonality: releaseNotesPersonality.default("pragmatic"),
  releaseNotesMarkerMode: z.enum(["emoji", "shield"]),
  releaseNotesCategories: z.array(releaseNotesCategory).min(1),
  releaseNotesFallbackCategory,
});
export type ReleaseConfiguration = z.infer<typeof releaseConfiguration>;

export const commitInput = z.object({
  sha: z.string().min(1),
  message: z.string(),
  author: z.string(),
  email: z.string(),
  timestamp: z.string(),
});
export const buildContextInput = z.object({
  configuration: z.string().nullish(),
  environment: z.string().nullish(),
  note: z.string().nullish(),
  groupId: z.string().trim().min(1).max(255).nullish(),
  tag: z.string().nullish(),
  targets: z.array(z.string()).default([]),
  force: z.boolean().default(false),
  retry: z.boolean().default(false),
});
export const runInput = z.object({
  name: z.string(),
  repository: z.string(),
  isTag: z.boolean(),
  runId: z.string().optional(),
  id: z.string().optional(),
  attempt: z.number().int(),
  actorName: z.string().nullish(),
  actorId: z.string().nullish(),
  otherCommits: z.array(commitInput).default([]),
});
export const startBuildInput = z.object({
  branchOrTag: z.string(),
  commit: commitInput,
  commitLog: z.array(commitInput).default([]),
  run: runInput.nullish(),
  context: buildContextInput,
});
export const completeBuildInput = z.object({ success: z.boolean() });
export const buildGraphNodeInput = z
  .object({
    id: z.string().trim().min(1),
    projectId: z.string().trim().min(1),
    projectName: z.string().trim().min(1).nullish(),
    projectDir: z.string(),
    target: z.string().trim().min(1),
    phase: z.string().nullish(),
    projectHash: z.string().trim().min(1),
    targetHash: z.string().trim().min(1),
    dependencies: z.array(z.string()).default([]),
    targetHashResolved: z.boolean().nullish(),
    phaseDependencies: z.array(z.string()).optional(),
    transformations: z.array(provenanceTransformation).optional(),
    artifacts: z.string(),
    build: z.string(),
    batch: z.string(),
    action: z.string(),
    required: z.boolean(),
    isBatchNode: z.boolean(),
  })
  .passthrough();
export const buildGraphInput = z
  .object({
    graphHash: z.string().default(""),
    environment: z.string().default(""),
    nodes: z.array(buildGraphNodeInput).default([]),
  })
  .passthrough();
export const graphInput = buildGraphInput;
export type BuildGraphInput = z.infer<typeof buildGraphInput>;
export type BuildGraphNodeInput = z.infer<typeof buildGraphNodeInput>;
export const artifactFile = z.union([
  z.string(),
  z.object({
    path: z.string(),
    checksum: z.string().optional(),
    size: z.number().int().nonnegative().optional(),
  }),
]);
export const addArtifactInput = z.object({
  project: z.string(),
  target: z.string(),
  projectHash: z.string(),
  targetHash: z.string(),
  files: z.array(artifactFile),
  success: z.boolean().default(true),
  startedAt: z.string().nullish(),
  endedAt: z.string().nullish(),
  productionId: z.string().min(1).max(100).nullish(),
  declaration: provenanceDeclaration.nullish(),
});
export const useArtifactInput = z.object({
  projectHash: z.string(),
  targetHash: z.string(),
  productionId: z.string().min(1).max(100).nullish(),
});
export const artifactOperation = z.enum(["head", "get", "put"]);
export const loginInput = z.object({
  id: workspaceId,
  token: z.string().min(1).max(100),
});

export interface BuildSummary {
  id: string;
  workspaceId: string;
  repository: string | null;
  branchOrTag: string;
  environment: string | null;
  actorName: string | null;
  startedAt: string;
  endedAt: string | null;
  success: boolean;
}

export type AtAGlanceBucketSize = "week" | "month";

export type AtAGlanceGainCoverage = "complete" | "partial";

export interface AtAGlanceBucket {
  from: string;
  to: string;
  pullRequests: number;
  builds: number;
  releases: number;
  savedBuildTimeMs: number;
  actualBuildTimeMs: number;
  reusedArtifactCount: number;
  unmeasuredArtifactCount: number;
  gainCoverage: AtAGlanceGainCoverage;
}

export interface AtAGlanceResponse {
  bucketSize: AtAGlanceBucketSize;
  buckets: AtAGlanceBucket[];
  scopeOptions: WorkspaceAnalyticsScopeOptions;
  scopeNote: string | null;
}

export type EngineeringHealthSection =
  | "flow"
  | "feedback"
  | "delivery"
  | "behavior";

export type EngineeringHealthMetricId =
  | "pr-lifetime"
  | "first-feedback"
  | "first-formal-review"
  | "open-pr-wip"
  | "stale-prs"
  | "build-duration"
  | "build-success"
  | "build-recovery"
  | "release-interval"
  | "merge-to-release"
  | "pr-size"
  | "concurrent-pr-wip"
  | "review-concentration";

export interface EngineeringHealthDistribution {
  p10: number;
  p50: number;
  p75: number;
  p90: number;
}

export interface EngineeringHealthSeriesPoint {
  from: string;
  to: string;
  value: number | null;
  sampleCount: number;
}

export interface EngineeringHealthMetric {
  id: EngineeringHealthMetricId;
  label: string;
  unit: "milliseconds" | "count" | "percent" | "lines";
  status: "available" | "insufficient-data" | "unavailable";
  value: number | null;
  sampleCount: number;
  distribution: EngineeringHealthDistribution | null;
  series: EngineeringHealthSeriesPoint[];
  previousValue: number | null;
  changePercent: number | null;
  outlierCount: number;
  unavailableReason: string | null;
}

export interface EngineeringHealthObservation {
  metric: EngineeringHealthMetricId;
  text: string;
}

export interface EngineeringHealthOutlier {
  metric: EngineeringHealthMetricId;
  identity: string;
  value: number;
  cohortValue: number;
  sampleCount: number;
  direction: "below" | "above";
}

export interface WorkspaceAnalyticsScopeOptions {
  repositories: string[];
  teams: Array<{ id: string; name: string }>;
  contributors: string[];
}

export type EngineeringHealthScopeOptions = WorkspaceAnalyticsScopeOptions;

export interface EngineeringHealthResponse {
  section: EngineeringHealthSection;
  period: 30 | 90 | 180;
  current: { from: string; to: string };
  previous: { from: string; to: string };
  metrics: EngineeringHealthMetric[];
  observations: EngineeringHealthObservation[];
  outliers: EngineeringHealthOutlier[];
  scopeOptions: EngineeringHealthScopeOptions;
  scopeNote: string | null;
}

export interface EngineeringHealthArtifact {
  id: string;
  kind:
    | "pull-request"
    | "comment"
    | "review"
    | "build"
    | "recovery"
    | "release"
    | "contributor";
  title: string;
  repository: string | null;
  contributor: string | null;
  occurredAt: string;
  value: number | null;
  url: string | null;
  detail: string | null;
}

export interface EngineeringHealthArtifactsPage {
  items: EngineeringHealthArtifact[];
  nextCursor: string | null;
}

export type EngineeringFindingKind =
  | "provenance-gap"
  | "high-churn"
  | "maintenance-risk"
  | "ownership-concentration"
  | "review-bottleneck"
  | "removal-candidate";

export type EngineeringFindingState =
  | "new"
  | "acknowledged"
  | "resolved"
  | "dismissed";

export interface EngineeringFindingEvidence {
  label: string;
  value: string;
  detail: string | null;
  url: string | null;
}

export interface EngineeringFinding {
  id: string;
  kind: EngineeringFindingKind;
  repository: string;
  scopeType: "project" | "repository";
  scopeKey: string;
  scopeLabel: string;
  severity: "high" | "medium" | "low";
  confidence: "high" | "medium" | "low";
  title: string;
  explanation: string;
  suggestedInvestigation: string;
  evidence: EngineeringFindingEvidence[];
  currentValue: number | null;
  previousValue: number | null;
  changePercent: number | null;
  sampleCount: number;
  state: EngineeringFindingState;
  dismissalReason: string | null;
  firstObservedAt: string;
  lastObservedAt: string;
  computedAt: string;
}

export interface EngineeringFindingsResponse {
  period: 90;
  findings: EngineeringFinding[];
  scopeOptions: WorkspaceAnalyticsScopeOptions;
  lastComputedAt: string | null;
  coverageNote: string;
}

export interface UpdateEngineeringFindingInput {
  state: EngineeringFindingState;
  dismissalReason?: string | null;
}

export interface CollaborationPullRequest {
  repository: string;
  number: number;
  title: string;
  url: string | null;
  reviewStates: string[];
}

export interface CollaborationActor {
  login: string;
  authoredPullRequests: number;
  reviewedPullRequests: number;
  peerReviewedPullRequests: number;
  reviewCoveragePercent: number | null;
}

export interface CollaborationRelationship {
  author: string;
  reviewer: string;
  pullRequestCount: number;
  authorCoveragePercent: number;
  pullRequests: CollaborationPullRequest[];
}

export interface CollaborationResponse {
  period: 90;
  from: string;
  to: string;
  actors: CollaborationActor[];
  relationships: CollaborationRelationship[];
  pullRequestCount: number;
  peerReviewedPullRequestCount: number;
  scopeNote: string;
}

export type InvolvementPeriod = 30 | 90 | 180;

export interface InvolvementProjectOption {
  name: string;
  directory: string;
}

export interface InvolvementOptionsResponse {
  repositories: string[];
  projects: InvolvementProjectOption[];
}

export interface InvolvementSeriesPoint {
  from: string;
  to: string;
  people: number;
}

export interface InvolvementContributor {
  identity: string;
  name: string;
  buildCount: number;
  activeWeeks: number;
  lastBuildAt: string;
}

export interface InvolvementResponse {
  period: InvolvementPeriod;
  current: { from: string; to: string };
  previous: { from: string; to: string };
  repository: string;
  project: InvolvementProjectOption;
  people: number;
  previousPeople: number | null;
  changePercent: number | null;
  comparisonAvailable: boolean;
  series: InvolvementSeriesPoint[];
  contributors: InvolvementContributor[];
  collectionStartedAt: string | null;
  unattributedBuildCount: number;
}

export type TimelineHistoryKind =
  | "initial"
  | "forward"
  | "rollback"
  | "divergent";

export type TimelineProjectChangeKind = "added" | "changed" | "removed";

export interface TimelineOption {
  profile: string;
  repository: string;
  environment: string;
  latestDeployedAt: string;
}

export interface TimelineCommit {
  sha: string;
  title: string;
  message: string;
  author: string;
  email: string;
  timestamp: string;
}

export interface TimelineProjectChange {
  projectId: string;
  projectName: string;
  projectDir: string;
  change: TimelineProjectChangeKind;
  previousHash: string | null;
  currentHash: string | null;
}

export interface TimelineBuild {
  buildId: string;
  deployedAt: string;
  targets: string[];
}

export interface TimelineEntry {
  buildId: string;
  builds: TimelineBuild[];
  deployedAt: string;
  branchOrTag: string;
  actorName: string | null;
  targets: string[];
  head: TimelineCommit;
  historyKind: TimelineHistoryKind;
  historyComplete: boolean;
  projects: TimelineProjectChange[];
  introducedCommits: TimelineCommit[];
  removedCommits: TimelineCommit[];
}

export interface TimelinePage {
  repository: string;
  environment: string;
  items: TimelineEntry[];
  nextCursor: string | null;
}

export interface ReleaseNotesScope {
  repository: string;
  profile: string;
  environment: string;
  branchPattern: string;
  latestObservedAt: string;
}

export interface ReleasePoint {
  id: string;
  kind: "tag" | "head";
  buildId: string;
  observedAt: string;
  branchOrTag: string;
  tag: string | null;
  note: string | null;
  head: TimelineCommit;
}

export interface ReleaseNotesResult {
  repository: string;
  profile: string;
  environment: string;
  fromPoint: ReleasePoint;
  toPoint: ReleasePoint;
  historyComplete: boolean;
  introducedCommits: TimelineCommit[];
  removedCommits: TimelineCommit[];
}
export * from "./featstudio";

export type ReleaseNotesContext = "timeline" | "comparison";

const releaseNotesSelection = {
  repository: z.string().trim().min(1).max(255),
  profile: z.string().trim().min(1).max(100),
};
export const generateReleaseNotesRequest = z.discriminatedUnion("context", [
  z.object({
    ...releaseNotesSelection,
    context: z.literal("timeline"),
    buildId: z.string().min(1).max(100),
  }),
  z.object({
    ...releaseNotesSelection,
    context: z.literal("comparison"),
    from: z.string().min(1).max(255),
    to: z.string().min(1).max(255),
  }),
]);
export type GenerateReleaseNotesRequest = z.infer<typeof generateReleaseNotesRequest>;
export interface GeneratedReleaseNotes {
  markdown: string;
  html: string;
  personality: ReleaseNotesPersonality;
  model: string | null;
  cache: "hit" | "miss" | "disabled" | "factual";
}

export type WorkspaceFeature = "featstudio" | "decisionInbox" | "provenance";

export interface WorkspaceFeatures {
  featstudio: boolean;
  decisionInbox: boolean;
  provenance: boolean;
}

export const releaseNotesAiSettings = z.object({
  model: z.string().regex(/^@cf\/[a-z0-9._-]+\/[a-z0-9._-]+$/).max(200),
  cachingEnabled: z.boolean(),
  globalPrompt: z.string().trim().min(1).max(20000).optional(),
  pragmaticPrompt: z.string().trim().min(1).max(20000).optional(),
  playfulPrompt: z.string().trim().min(1).max(20000).optional(),
});
export type ReleaseNotesAiSettings = z.infer<typeof releaseNotesAiSettings>;
